<script setup lang="ts">
import { getMonthName } from '~/shared/utils/formatDate';
import { parseLocalDateTime } from '~/shared/utils/parseLocalDateTime';

const props = withDefaults(
  defineProps<{
    startTime: string;
    endTime?: string;
    monthOnly?: boolean;
    variant: 'event-card' | 'events-banner' | 'event-hero';
  }>(),
  {
    endTime: undefined,
    monthOnly: false,
  },
);

const startParts = computed(() => parseLocalDateTime(props.startTime));
const endParts = computed(() =>
  props.endTime ? parseLocalDateTime(props.endTime) : null,
);

const isMultiDay = computed(() => {
  if (props.monthOnly || !endParts.value) return false;
  const s = startParts.value;
  const e = endParts.value;
  return s.year !== e.year || s.month !== e.month || s.day !== e.day;
});

const day = computed(() => String(startParts.value.day).padStart(2, '0'));

// Names up to four letters fit every badge size in full
const MAX_FULL_MONTH_LENGTH = 4;

const month = computed(() => {
  const index = startParts.value.month - 1;
  if (!props.monthOnly) return getMonthName(index, 'short');

  const full = getMonthName(index, 'nominative');
  return full.length <= MAX_FULL_MONTH_LENGTH
    ? full
    : getMonthName(index, 'shortNominative');
});

const endLabel = computed(() => {
  if (!endParts.value) return '';
  const endDay = String(endParts.value.day).padStart(2, '0');
  const endMonth = getMonthName(endParts.value.month - 1, 'short');
  return `по ${endDay} ${endMonth}`;
});

const isPast = computed(() => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const ref = endParts.value || startParts.value;
  const refDate = new Date(ref.year, ref.month - 1, ref.day);
  return refDate < today;
});
</script>

<template>
  <div
    :class="[
      $style.badge,
      $style[`badge_variant_${props.variant}`],
      {
        [$style.badge_multi!]: isMultiDay,
        [$style.badge_monthOnly!]: monthOnly,
        [$style.badge_past!]: isPast,
      },
    ]"
  >
    <span v-if="!monthOnly" :class="$style.day">{{ day }}</span>
    <span :class="$style.month">{{ month }}</span>
    <span v-if="isMultiDay" :class="$style.endLabel">{{ endLabel }}</span>
  </div>
</template>

<style module lang="scss">
.badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: var(--color-event-accent);
  color: var(--color-white);
  text-transform: uppercase;
  font-family: var(--font-golos);
  font-weight: 400;
  text-align: center;
  position: relative;
  flex-shrink: 0;

  &_past {
    background-color: var(--color-gray-text);
  }

  &_monthOnly {
    justify-content: center;
  }
}

.badge_variant_event-hero,
.badge_variant_events-banner {
  width: 80px;
  height: 80px;
}

.badge_variant_event-card {
  width: 52px;
  height: 52px;
}

.day {
  line-height: 1;
}

.badge_variant_event-hero .day,
.badge_variant_events-banner .day {
  font-size: 38px;
  margin-top: 2px;
}

.badge_variant_event-card .day {
  font-size: 24px;
  margin-top: 5px;
}

.month {
  line-height: 1;
}

.badge_variant_event-hero .month,
.badge_variant_events-banner .month {
  font-size: 20px;
  margin-top: 3px;
}

.badge_variant_event-card .month {
  font-size: 13px;
  margin-top: 3px;
}

.endLabel {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: var(--color-gray-bg);
  color: var(--color-black-1);
  text-align: center;
  text-transform: uppercase;
  line-height: 1;
  padding: 2px;
  white-space: nowrap;
}

.badge_variant_event-hero .endLabel,
.badge_variant_events-banner .endLabel {
  font-size: 10px;
}

.badge_variant_event-card .endLabel {
  font-size: 9px;
  display: none;
}

@media (max-width: 1379px) and (min-width: 480px) {
  .badge_variant_event-card {
    height: 62px;
  }

  .badge_variant_event-card .day {
    margin-top: 9px;
  }

  .badge_variant_event-card .month {
    margin-top: 4px;
  }
}

@media (max-width: 1379px) {
  .badge_variant_events-banner {
    width: 60px;
    height: 68px;
  }

  .badge_variant_events-banner .day {
    font-size: 28px;
    margin-top: 4px;
  }

  .badge_variant_events-banner .month {
    font-size: 15px;
    margin-top: 2px;
  }

  .badge_variant_events-banner .endLabel {
    font-size: 9px;
  }
}

@media (max-width: 479px) {
  .badge_variant_event-hero {
    width: 60px;
    height: 68px;
  }

  .badge_variant_event-hero .day {
    font-size: 28px;
    margin-top: 4px;
  }

  .badge_variant_event-hero .month {
    font-size: 15px;
    margin-top: 2px;
  }

  .badge_variant_event-hero .endLabel {
    font-size: 9px;
  }

  .badge_variant_event-card {
    width: 56px;
    height: 62px;
  }

  .badge_variant_event-card .day {
    margin-top: 5px;
  }

  .badge_variant_event-card .month {
    margin-top: 4px;
  }

  .badge_variant_event-card .endLabel {
    display: block;
    background-color: var(--color-yellow-1);
    color: var(--color-white);
  }
}

.badge.badge_monthOnly .month {
  margin-top: 0;
}
</style>
